import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { XMarkIcon, MagnifyingGlassPlusIcon } from "@heroicons/react/24/outline";

type ScreenshotProps = {
    src: string;
    /** Rendered under the image and used as the `alt` text. */
    caption?: string;
    /** When set, a numbered badge ties the image to a Quick Start step. */
    step?: number;
    /** Print mode drops the zoom affordance — paper has no click target. */
    print?: boolean;
};

/**
 * A documentation screenshot.
 *
 * `ui/Modal` is deliberately not reused for the lightbox: it is a light-themed,
 * width-capped dashboard dialog, whereas a screenshot wants a full-bleed dark
 * overlay on top of the dark docs surface.
 *
 * The `onError` placeholder matters more than it looks — screenshots are
 * produced by `npm run docs:screenshots` against a live dashboard, so a fresh
 * clone renders every figure before any PNG exists. A labelled placeholder
 * keeps those pages (and the PDF) readable instead of littering them with
 * broken-image glyphs.
 */
export default function Screenshot({ src, caption, step, print = false }: ScreenshotProps) {
    const [zoomed, setZoomed] = useState(false);
    const [failed, setFailed] = useState(false);

    const close = useCallback(() => setZoomed(false), []);

    useEffect(() => {
        if (!zoomed) return;
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") close();
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [zoomed, close]);

    const stepBadge =
        typeof step === "number" ? (
            <span className="absolute left-3 top-3 z-10 inline-flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-r from-orange-500 to-pink-500 text-sm font-semibold text-white shadow-lg shadow-orange-500/30 print:shadow-none">
                {step}
            </span>
        ) : null;

    return (
        <figure className="docs-figure my-7 print:my-4">
            <div className="relative overflow-hidden rounded-xl border border-slate-800 bg-slate-900/60 print:rounded-md print:border-slate-300 print:bg-white">
                {stepBadge}

                {failed ? (
                    <div className="flex min-h-[9rem] flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-700 px-6 py-10 text-center print:border-slate-400">
                        <span className="text-sm font-medium text-slate-300 print:text-slate-700">Screenshot pending capture</span>
                        <code className="text-xs text-slate-500 print:text-slate-500">{src}</code>
                        <span className="text-xs text-slate-500 print:text-slate-500">Run npm run docs:screenshots to generate it.</span>
                    </div>
                ) : print ? (
                    <img src={src} alt={caption ?? ""} className="w-full" onError={() => setFailed(true)} />
                ) : (
                    <button
                        type="button"
                        onClick={() => setZoomed(true)}
                        className="group relative block w-full cursor-zoom-in"
                        aria-label={caption ? `Enlarge screenshot: ${caption}` : "Enlarge screenshot"}
                    >
                        <img src={src} alt={caption || "FloGPT documentation screenshot"} className="w-full" loading="lazy" decoding="async" onError={() => setFailed(true)} />
                        <span className="pointer-events-none absolute right-3 top-3 inline-flex items-center gap-1 rounded-lg bg-slate-950/70 px-2 py-1 text-xs text-slate-200 opacity-0 backdrop-blur transition-opacity group-hover:opacity-100">
                            <MagnifyingGlassPlusIcon className="h-3.5 w-3.5" />
                            Enlarge
                        </span>
                    </button>
                )}
            </div>

            {caption ? (
                <figcaption className="mt-2 text-sm text-slate-400 print:text-slate-600">
                    {typeof step === "number" ? <span className="font-medium text-slate-300 print:text-slate-800">Step {step}. </span> : null}
                    {caption}
                </figcaption>
            ) : null}

            {zoomed && !print
                ? createPortal(
                      <div
                          className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/90 p-4 backdrop-blur-sm sm:p-8"
                          onClick={close}
                          role="dialog"
                          aria-modal="true"
                          aria-label={caption ?? "Screenshot"}
                      >
                          <button
                              type="button"
                              onClick={close}
                              className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white transition hover:bg-white/20"
                              aria-label="Close"
                          >
                              <XMarkIcon className="h-5 w-5" />
                          </button>
                          <img
                              src={src}
                              alt={caption ?? ""}
                              className="max-h-full max-w-full rounded-lg border border-slate-700 object-contain"
                              onClick={(event) => event.stopPropagation()}
                          />
                      </div>,
                      document.body,
                  )
                : null}
        </figure>
    );
}
